import { Button, Result, Spin, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { cadastroCotaUsuarioApi } from "./cadastroCotaUsuario.api";
import { EtapaCadastroCota, FluxoResposta, NoFluxoCadastroCota } from "./cadastroCotaUsuario.types";
import { etapaDoNo, EtapaCadastroRenderer } from "./EtapaCadastroRenderer";
import {
  getApiErrorMessage,
  getEtapaAtualResumo,
  isForbiddenError,
  isNotFoundError,
  isUnauthorizedError,
  PageSection,
} from "./utils";

let pendingStartPromise: Promise<FluxoResposta> | null = null;

async function aplicarStatusDaAssinatura(
  fluxo: FluxoResposta
): Promise<FluxoResposta> {
  if (fluxo.noAtual === "PAGAMENTO_PENDENTE") {
    return { ...fluxo, etapaAtual: "PAGAMENTO_PENDENTE" };
  }

  if (fluxo.noAtual === "PAGAMENTO_CONFIRMADO") {
    return { ...fluxo, etapaAtual: "PAGAMENTO_CONFIRMADO" };
  }

  if (
    fluxo.noAtual === "UTILIZAR_VAGA_ASSINATURA" &&
    fluxo.etapaAtual === "AGUARDANDO_CONTRATACAO"
  ) {
    return { ...fluxo, etapaAtual: "UTILIZAR_VAGA_ASSINATURA" };
  }

  if (fluxo.noAtual !== "CONTRATACAO") {
    return fluxo;
  }

  const cotaUsuarioId = fluxo.cotaUsuarioId ?? fluxo.contextoFluxo.cotaUsuarioId;
  if (!cotaUsuarioId) {
    return fluxo;
  }

  try {
    const status = await cadastroCotaUsuarioApi.buscarStatusAssinatura(cotaUsuarioId);
    const statusNormalizado = status?.toUpperCase();

    if (statusNormalizado === "PENDENTE" || statusNormalizado === "AGUARDANDO_PAGAMENTO") {
      return { ...fluxo, etapaAtual: "PAGAMENTO_PENDENTE" };
    }

    if (statusNormalizado === "ATIVA" || statusNormalizado === "ATIVO") {
      return { ...fluxo, etapaAtual: "PAGAMENTO_CONFIRMADO" };
    }
  } catch {
    // Sem o status da assinatura, preserva a etapa retornada pelo fluxo.
  }

  return fluxo;
}

function periodoJaDefinidoDoBackend(dadosPreenchidos?: Record<string, unknown>) {
  const dados = dadosPreenchidos as
      | {
        periodoJaDefinido?: boolean;
        periodoNaoDefinido?: boolean;
        periodosJaDefinidos?: boolean;
        periodosAindaNaoDefinidos?: boolean;
      }
    | undefined;
  const existencia = dadosPreenchidos?.existenciaPeriodos as
    | {
        periodoJaDefinido?: boolean;
        periodoNaoDefinido?: boolean;
        periodosJaDefinidos?: boolean;
        periodosAindaNaoDefinidos?: boolean;
      }
    | undefined;

  if (typeof (existencia?.periodoJaDefinido ?? dados?.periodoJaDefinido) === "boolean") {
    return existencia?.periodoJaDefinido ?? dados?.periodoJaDefinido;
  }
  if (typeof existencia?.periodosJaDefinidos === "boolean") {
    return existencia.periodosJaDefinidos;
  }
  if (typeof existencia?.periodosAindaNaoDefinidos === "boolean") {
    return !existencia.periodosAindaNaoDefinidos;
  }
  if (typeof (existencia?.periodoNaoDefinido ?? dados?.periodoNaoDefinido) === "boolean") {
    return !(existencia?.periodoNaoDefinido ?? dados?.periodoNaoDefinido);
  }
  return undefined;
}

function getLinkPagamento(fluxo: FluxoResposta) {
  const venda = fluxo.dadosPreenchidos?.venda as
    | {
        checkout?:
          | string
          | { linkPagamento?: string; url?: string; checkoutUrl?: string };
      }
    | undefined;
  const checkout = venda?.checkout;

  if (typeof checkout === "string") {
    return checkout;
  }

  return checkout?.linkPagamento ?? checkout?.url ?? checkout?.checkoutUrl;
}

export default function ViewCadastroPropriedadeV2() {
  const { cadastroCotaUsuarioId } = useParams();
  const navigate = useNavigate();
  const [fluxo, setFluxo] = useState<FluxoResposta | null>(null);
  const [historicoFluxo, setHistoricoFluxo] = useState<NoFluxoCadastroCota[]>([]);
  const [etapaExibida, setEtapaExibida] = useState<EtapaCadastroCota | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erroInicial, setErroInicial] = useState<string | null>(null);
  const [acessoNegado, setAcessoNegado] = useState(false);
  const [cadastroNaoEncontrado, setCadastroNaoEncontrado] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (etapaExibida) {
      window.scrollTo(0, 0);
    }
  }, [etapaExibida, fluxo?.noAtual]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const atualizarAoRetornarDoCheckout = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener("pageshow", atualizarAoRetornarDoCheckout);

    return () => {
      window.removeEventListener("pageshow", atualizarAoRetornarDoCheckout);
    };
  }, []);

  useEffect(() => {
    if (!acessoNegado) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      navigate("/cadastroCotas", { replace: true });
    }, 3000);

    return () => window.clearTimeout(timeoutId);
  }, [acessoNegado, navigate]);

  useEffect(() => {
    let active = true;

    async function carregarFluxo() {
      setCarregando(true);
      setErroInicial(null);
      setAcessoNegado(false);
      setCadastroNaoEncontrado(false);

      try {
        if (cadastroCotaUsuarioId) {
          const response = await cadastroCotaUsuarioApi.consultarFluxo(
            Number(cadastroCotaUsuarioId)
          );

          if (!active || !mountedRef.current) {
            return;
          }

          const fluxoComStatusDaAssinatura = await aplicarStatusDaAssinatura(response);
          setFluxo(fluxoComStatusDaAssinatura);
          setHistoricoFluxo([fluxoComStatusDaAssinatura.noAtual]);
          setEtapaExibida(fluxoComStatusDaAssinatura.etapaAtual);
          return;
        }

        if (!pendingStartPromise) {
          pendingStartPromise = cadastroCotaUsuarioApi.iniciarCadastro();
        }

        const response = await pendingStartPromise;

        if (!active || !mountedRef.current) {
          return;
        }

        pendingStartPromise = null;
        navigate(`/cadastroPropriedade/${response.cadastroCotaUsuarioId}`, {
          replace: true,
        });
      } catch (error) {
        pendingStartPromise = null;

        if (!active || !mountedRef.current) {
          return;
        }

        if (isUnauthorizedError(error)) {
          navigate("/login", { replace: true });
          return;
        }

        if (isForbiddenError(error)) {
          setAcessoNegado(true);
          return;
        }

        if (isNotFoundError(error)) {
          setCadastroNaoEncontrado(true);
          return;
        }

        setErroInicial(
          getApiErrorMessage(
            error,
            "Não foi possível carregar o onboarding de cadastro."
          )
        );
      } finally {
        if (active && mountedRef.current) {
          setCarregando(false);
        }
      }
    }

    carregarFluxo();

    return () => {
      active = false;
    };
  }, [cadastroCotaUsuarioId, navigate]);

  async function consultarENavegar(noDestino: NoFluxoCadastroCota) {
    if (!fluxo || carregando) {
      return;
    }

    setCarregando(true);

    try {
      const resposta = await cadastroCotaUsuarioApi.consultarFluxo(
        fluxo.cadastroCotaUsuarioId
      );
      const indiceDestino = resposta.trilha.indexOf(noDestino);
      const noAnterior = indiceDestino > 0
        ? resposta.trilha[indiceDestino - 1]
        : null;
      const fluxoNavegado: FluxoResposta = {
        ...resposta,
        etapaAtual: etapaDoNo[noDestino],
        noAtual: noDestino,
        noAnterior,
        etapaAnterior: noAnterior ? etapaDoNo[noAnterior] : null,
        detalhesEtapa: undefined,
      };

      const fluxoComStatusDaAssinatura = await aplicarStatusDaAssinatura(fluxoNavegado);
      setFluxo(fluxoComStatusDaAssinatura);
      setHistoricoFluxo([noDestino]);
      setEtapaExibida(fluxoComStatusDaAssinatura.etapaAtual);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        navigate("/login", { replace: true });
        return;
      }
      if (isForbiddenError(error)) {
        setAcessoNegado(true);
        return;
      }
      if (isNotFoundError(error)) {
        setCadastroNaoEncontrado(true);
        return;
      }
      message.error(
        getApiErrorMessage(error, "Não foi possível atualizar o fluxo.")
      );
    } finally {
      setCarregando(false);
    }
  }

  async function concluirEtapa(payload: Record<string, unknown>) {
    if (!fluxo || !etapaExibida || carregando) {
      return;
    }

    const disponibilidadeSalva = fluxo.dadosPreenchidos?.disponibilidade as
      | Record<string, unknown>
      | undefined;
    const disponibilidadeSalvaEscopo =
      disponibilidadeSalva?.escopoDisponibilizacao ??
      (disponibilidadeSalva?.motivo
        ? "NAO_DESEJO_RECEBER_OPORTUNIDADES"
        : fluxo.contextoFluxo.escopoDisponibilizacao);
    const motivoDisponibilidadeSemAlteracao =
      payload.escopoDisponibilizacao !== "NAO_DESEJO_RECEBER_OPORTUNIDADES" ||
      (payload.motivo === disponibilidadeSalva?.motivo &&
        (payload.motivo !== "OUTRO" ||
          payload.observacao === disponibilidadeSalva?.observacao));
    const disponibilidadeSemAlteracao =
      etapaExibida === "AGUARDANDO_DISPONIBILIDADE" &&
      fluxo.noAtual === "DISPONIBILIDADE" &&
      typeof disponibilidadeSalvaEscopo === "string" &&
      payload.escopoDisponibilizacao === disponibilidadeSalvaEscopo &&
      motivoDisponibilidadeSemAlteracao;
    const empreendimentoSalvo = fluxo.dadosPreenchidos?.empreendimento as
      | Record<string, unknown>
      | undefined;
    const empreendimentoSemAlteracao =
      etapaExibida === "AGUARDANDO_EMPREENDIMENTO" &&
      fluxo.noAtual === "EMPREENDIMENTO" &&
      Boolean(empreendimentoSalvo) &&
      (payload.modo === "EXISTENTE"
        ? empreendimentoSalvo?.modo === "EXISTENTE" &&
          empreendimentoSalvo.empreendimentoId === payload.empreendimentoId
        : empreendimentoSalvo?.modo === "NOVO" &&
          empreendimentoSalvo.nome === payload.nome &&
          empreendimentoSalvo.cidadeId === payload.cidadeId);
    const apartamentoSalvo = fluxo.dadosPreenchidos?.apartamento as
      | Record<string, unknown>
      | undefined;
    const apartamentoNovo = payload.apartamento as
      | Record<string, unknown>
      | undefined;
    const apartamentoSalvoNovo = apartamentoSalvo?.apartamento as
      | Record<string, unknown>
      | undefined;
    const apartamentoSemAlteracao =
      etapaExibida === "AGUARDANDO_APARTAMENTO" &&
      fluxo.noAtual === "APARTAMENTO" &&
      Boolean(apartamentoSalvo) &&
      (payload.modo === "EXISTENTE"
        ? apartamentoSalvo?.modo === "EXISTENTE" &&
          apartamentoSalvo.aptoId === payload.aptoId
        : apartamentoSalvo?.modo === "NOVO" &&
          Object.entries(apartamentoNovo ?? {}).every(
            ([campo, valor]) =>
              (apartamentoSalvoNovo?.[campo] ?? apartamentoSalvo?.[campo]) === valor
          ));
    const periodosJaDefinidosSalvo = periodoJaDefinidoDoBackend(
      fluxo.dadosPreenchidos
    );
    const existenciaSemAlteracao =
      etapaExibida === "AGUARDANDO_PERIODOS" &&
      fluxo.noAtual === "EXISTENCIA_PERIODOS" &&
      typeof periodosJaDefinidosSalvo === "boolean" &&
      payload.periodosJaDefinidos === periodosJaDefinidosSalvo;

    const identificacaoSalva =
      (fluxo.dadosPreenchidos?.identificacaoCota ??
        fluxo.dadosPreenchidos?.cota) as Record<string, unknown> | undefined;
    const identificacaoSemAlteracao =
      etapaExibida === "AGUARDANDO_IDENTIFICACAO_COTA" &&
      fluxo.noAtual === "IDENTIFICACAO_COTA" &&
      Boolean(identificacaoSalva) &&
      (payload.naoSei === true
        ? identificacaoSalva?.naoSeiNumeroCota === true ||
          identificacaoSalva?.numeroCota === null
        : payload.cotaId !== undefined &&
          identificacaoSalva?.cotaId === payload.cotaId);

    const titularidadeSalva = fluxo.dadosPreenchidos?.titularidade as
      | { vinculos?: Array<Record<string, unknown>> }
      | undefined;
    const vinculoPrincipal = titularidadeSalva?.vinculos?.find(
      (vinculo) => vinculo.principal === true
    );
    const titularidadeSemAlteracao =
      etapaExibida === "AGUARDANDO_TITULARIDADE" &&
      fluxo.noAtual === "TITULARIDADE" &&
      Boolean(vinculoPrincipal) &&
      vinculoPrincipal?.pessoaId === payload.pessoaId &&
      vinculoPrincipal?.tipoVinculo === payload.tipoVinculo &&
      vinculoPrincipal?.principal === payload.principal;

    const vendaSalva = fluxo.dadosPreenchidos?.venda as
      | Record<string, unknown>
      | undefined;
    const vagaAssinaturaSemAlteracao =
      fluxo.noAtual === "UTILIZAR_VAGA_ASSINATURA" &&
      typeof vendaSalva?.desejaUtilizarVagaAssinatura === "boolean" &&
      payload.desejaUtilizarVagaAssinatura ===
        vendaSalva.desejaUtilizarVagaAssinatura;
    const vendaSemAlteracao =
      etapaExibida === "AGUARDANDO_VENDA" &&
      fluxo.noAtual === "VENDA" &&
      typeof vendaSalva?.desejaVender === "boolean" &&
      vendaSalva.desejaVender === payload.desejaVender;
    const situacaoCotaSalva = (fluxo.dadosPreenchidos?.venda ??
      fluxo.dadosPreenchidos?.situacaoCota) as Record<string, unknown> | undefined;
    const situacaoCotaSemAlteracao =
      etapaExibida === "AGUARDANDO_VENDA" &&
      (fluxo.noAtual === "SITUACAO_COTA" || fluxo.detalhesEtapa?.pergunta === "SITUACAO_COTA") &&
      Boolean(situacaoCotaSalva) && situacaoCotaSalva?.situacao === payload.situacao &&
      situacaoCotaSalva?.escriturada === payload.escriturada;
    const saldoDevedorSalvo = fluxo.dadosPreenchidos?.venda as Record<string, unknown> | undefined;
    const saldoDevedorSemAlteracao =
      etapaExibida === "AGUARDANDO_VENDA" &&
      (fluxo.noAtual === "SALDO_DEVEDOR" || fluxo.detalhesEtapa?.pergunta === "SALDO_DEVEDOR") &&
      Boolean(saldoDevedorSalvo) &&
      ((payload.modo === "VALOR_TOTAL" &&
        saldoDevedorSalvo?.saldoDevedor === payload.saldoDevedor &&
        (saldoDevedorSalvo?.quantidadeParcelasDevedoras == null) &&
        (saldoDevedorSalvo?.valorParcela == null)) ||
        (payload.modo === "PARCELAS_RESTANTES" &&
          saldoDevedorSalvo?.quantidadeParcelasDevedoras === payload.parcelasRestantes &&
          saldoDevedorSalvo?.valorParcela === payload.valorParcela));
    const valorVendaSalvo = fluxo.dadosPreenchidos?.venda as Record<string, unknown> | undefined;
    const valorVendaSemAlteracao =
      etapaExibida === "AGUARDANDO_VENDA" &&
      (fluxo.noAtual === "VALOR_VENDA" || fluxo.detalhesEtapa?.pergunta === "VALOR_VENDA") &&
      Boolean(valorVendaSalvo) &&
      valorVendaSalvo?.valorVenda === payload.valorVenda;
    const outrosDetalhesVendaSalvos = fluxo.dadosPreenchidos?.venda as Record<string, unknown> | undefined;
    const outrosDetalhesVendaSemAlteracao =
      etapaExibida === "AGUARDANDO_VENDA" &&
      (fluxo.noAtual === "OUTROS_DETALHES_VENDA" || fluxo.detalhesEtapa?.pergunta === "OUTROS_DETALHES_VENDA") &&
      Boolean(outrosDetalhesVendaSalvos) &&
      outrosDetalhesVendaSalvos?.aceitaTroca === payload.aceitaTroca &&
      outrosDetalhesVendaSalvos?.valorCondominio === payload.valorCondominio;

    const hospedagemSalva = fluxo.dadosPreenchidos
      ?.oportunidadesHospedagem as
      | {
          desejaReceberOportunidadesHospedagem?: boolean;
          alertaCriado?: boolean;
          cidadeIds?: number[];
        }
      | undefined;
    const cidadesPayload = Array.isArray(payload.cidadeIds)
      ? Array.from(new Set(payload.cidadeIds as number[])).sort((a, b) => a - b)
      : undefined;
    const cidadesSalvas = Array.isArray(hospedagemSalva?.cidadeIds)
      ? Array.from(new Set(hospedagemSalva?.cidadeIds)).sort((a, b) => a - b)
      : undefined;
    const hospedagemEscolhaSemAlteracao =
      etapaExibida === "AGUARDANDO_OPORTUNIDADES_HOSPEDAGEM" &&
      fluxo.noAtual === "OPORTUNIDADES_HOSPEDAGEM" &&
      typeof hospedagemSalva?.desejaReceberOportunidadesHospedagem ===
        "boolean" &&
      payload.desejaReceberOportunidadesHospedagem ===
        hospedagemSalva.desejaReceberOportunidadesHospedagem;
    const alertaHospedagemSemAlteracao =
      etapaExibida === "AGUARDANDO_OPORTUNIDADES_HOSPEDAGEM" &&
      fluxo.noAtual === "OPORTUNIDADES_HOSPEDAGEM" &&
      payload.acao === "CRIAR_ALERTA_HOSPEDAGEM" &&
      hospedagemSalva?.alertaCriado === true &&
      Boolean(cidadesPayload) &&
      Boolean(cidadesSalvas) &&
      cidadesPayload!.length === cidadesSalvas!.length &&
      cidadesPayload!.every((cidadeId, index) => cidadeId === cidadesSalvas![index]);

    if (
      disponibilidadeSemAlteracao ||
      existenciaSemAlteracao ||
      empreendimentoSemAlteracao ||
      apartamentoSemAlteracao ||
      identificacaoSemAlteracao ||
      titularidadeSemAlteracao ||
      vendaSemAlteracao ||
      situacaoCotaSemAlteracao ||
      saldoDevedorSemAlteracao ||
      valorVendaSemAlteracao ||
      outrosDetalhesVendaSemAlteracao ||
      vagaAssinaturaSemAlteracao ||
      hospedagemEscolhaSemAlteracao ||
      alertaHospedagemSemAlteracao
    ) {
      const indiceNoAtual = fluxo.trilha.lastIndexOf(fluxo.noAtual);
      const proximoNo =
        indiceNoAtual >= 0
          ? fluxo.trilha[indiceNoAtual + 1]
          : undefined;

      if (proximoNo) {
        await consultarENavegar(proximoNo);
        return;
      }
    }

    /* const chaveDadosPorEtapa: Partial<Record<EtapaCadastroCota, string>> = {
      AGUARDANDO_EMPREENDIMENTO: "empreendimento",
      AGUARDANDO_APARTAMENTO: "apartamento",
      AGUARDANDO_IDENTIFICACAO_COTA: "identificacaoCota",
      AGUARDANDO_TITULARIDADE: "titularidade",
      AGUARDANDO_DISPONIBILIDADE: "disponibilidade",
      AGUARDANDO_PERIODOS: "periodos",
    };
    const etapasDaTrilha = fluxo.trilha.map((no) => etapaDoNo[no]);
    const indiceEtapa = etapasDaTrilha.indexOf(etapaExibida);
    const proximaEtapaLocal =
      indiceEtapa >= 0 ? etapasDaTrilha[indiceEtapa + 1] : undefined;
    const dadosDaEtapa = chaveDadosPorEtapa[etapaExibida]
      ? (fluxo.dadosPreenchidos?.[chaveDadosPorEtapa[etapaExibida]!] as
          | Record<string, unknown>
          | undefined)
      : undefined;
    const naoFoiAlterado =
      dadosDaEtapa &&
      Object.keys(dadosDaEtapa).length > 0 &&
      Object.entries(payload).every(([chave, valor]) => {
        if (etapaExibida === "AGUARDANDO_IDENTIFICACAO_COTA" && payload.naoSei === true && dadosDaEtapa.numeroCota === null && (chave === "naoSei" || chave === "cotaId")) return true;
        // Os campos do apartamento são retornados sem o agrupamento usado no envio.
        if (etapaExibida === "AGUARDANDO_APARTAMENTO" && chave === "apartamento" && valor && typeof valor === "object") {
          return Object.entries(valor).every(([campo, conteudo]) =>
            (dadosDaEtapa[campo] ?? null) === (conteudo ?? null)
          );
        }
        return dadosDaEtapa[chave] === valor;
      });

    if (naoFoiAlterado && proximaEtapaLocal) {
      setEtapaExibida(proximaEtapaLocal);
      return;
    }

    } */
    setCarregando(true);

    try {
      const etapaParaConclusao =
        fluxo.noAtual === "CONTRATACAO"
          ? "AGUARDANDO_VENDA"
          : fluxo.noAtual === "PAGAMENTO_PENDENTE" ||
              fluxo.noAtual === "PAGAMENTO_CONFIRMADO" ||
              fluxo.noAtual === "UTILIZAR_VAGA_ASSINATURA"
            ? "AGUARDANDO_CONTRATACAO"
          : etapaExibida;

      console.log("[CadastroCotaUsuario] Concluindo etapa", {
        cadastroCotaUsuarioId: fluxo.cadastroCotaUsuarioId,
        etapaAtual: etapaParaConclusao,
        payload,
      });

      let novoFluxo = await cadastroCotaUsuarioApi.concluirEtapa(
        fluxo.cadastroCotaUsuarioId,
        {
          etapaAtual: etapaParaConclusao,
          payload,
        }
      );

      if (typeof payload.desejaUtilizarVagaAssinatura === "boolean") {
        novoFluxo = await cadastroCotaUsuarioApi.consultarFluxo(
          fluxo.cadastroCotaUsuarioId
        );
      }

      if (payload.acao === "CONTRATAR_ASSINATURA") {
        const linkPagamento = getLinkPagamento(novoFluxo);

        if (linkPagamento) {
          window.location.assign(linkPagamento);
          return;
        }

        message.error(
          "Não foi possível obter o link de pagamento. Tente novamente."
        );
      }

      const fluxoComStatusDaAssinatura = await aplicarStatusDaAssinatura(novoFluxo);
      setFluxo(fluxoComStatusDaAssinatura);
      setHistoricoFluxo([fluxoComStatusDaAssinatura.noAtual]);
      setEtapaExibida(fluxoComStatusDaAssinatura.etapaAtual);
    } catch (error) {
      if (isUnauthorizedError(error)) {
        navigate("/login", { replace: true });
        return;
      }

      if (isForbiddenError(error)) {
        setAcessoNegado(true);
        return;
      }

      if (isNotFoundError(error)) {
        setCadastroNaoEncontrado(true);
        return;
      }

      message.error(
        getApiErrorMessage(error, "Não foi possível concluir esta etapa.")
      );
    } finally {
      setCarregando(false);
    }
  }

  if (acessoNegado) {
    return (
      <PageSection>
        <Result
          status="403"
          title="Você não possui permissão para acessar este cadastro"
          subTitle="Você será redirecionado para suas propriedades."
          extra={
            <Button onClick={() => navigate("/cadastroCotas", { replace: true })}>
              Ir para minhas propriedades
            </Button>
          }
        />
      </PageSection>
    );
  }

  if (cadastroNaoEncontrado) {
    return (
      <PageSection>
        <Result
          status="404"
          title="Não encontramos este cadastro"
          subTitle="Ele pode ter sido removido ou o link pode estar incorreto."
          extra={
            <Button onClick={() => navigate("/cadastroCotas", { replace: true })}>
              Ir para minhas propriedades
            </Button>
          }
        />
      </PageSection>
    );
  }

  if (erroInicial) {
    return (
      <PageSection>
        <Result
          status="error"
          title="Não foi possível abrir o onboarding"
          subTitle={erroInicial}
          extra={
            <Button onClick={() => window.location.reload()}>Tentar novamente</Button>
          }
        />
      </PageSection>
    );
  }

  if (!fluxo) {
    return (
      <PageSection style={{ display: "grid", placeItems: "center", minHeight: 320 }}>
        <Spin size="large" />
      </PageSection>
    );
  }

  const noAtualExibido = historicoFluxo[historicoFluxo.length - 1] ?? fluxo.noAtual;
  const indiceNoAtual = fluxo.trilha.lastIndexOf(noAtualExibido);
  const noAnterior =
    indiceNoAtual > 0
      ? fluxo.trilha[indiceNoAtual - 1]
      : fluxo.noAnterior ?? undefined;
  const etapaAnterior = noAnterior ? etapaDoNo[noAnterior] : undefined;
  const voltar = noAnterior
    ? () => { void consultarENavegar(noAnterior); }
    : undefined;

  return (
    <EtapaCadastroRenderer
      fluxo={fluxo}
      etapaExibida={etapaExibida ?? fluxo.etapaAtual}
      submitting={carregando}
      onConcluir={concluirEtapa}
      onVoltar={voltar}
      etapaAnteriorNome={
        etapaAnterior ? getEtapaAtualResumo(etapaAnterior).nome : undefined
      }
    />
  );
}
